import React, { useState, useEffect } from "react";
import { PageHeader } from "../../components/layout/header/Header";
import { StandardPageWrapper } from "../../components/layout/StandardPageWrapper";
import { SettingsTabs } from "./SettingsTabs";
import "../../../../sass/components/organization-billing-page.scss";
import { usePrimaryOrganization, useSelfOrganizationUser } from "../../hooks/useOrganization";
import Tooltip from "@mui/material/Tooltip";
import { InlineWidget } from "react-calendly";
import { Api } from "../../../common/service/Api"; // Adjust the import path as needed
import {
  ChargebeeCustomerInfoResponse,
  ChargebeeCardInfo,
} from "../../../common/service/server/Types";
import { lexicalJoin } from "../../util/text";
import { Button } from "@truevault/aquilo";
import { useSnackBar } from "@truevault/aquilo";

export const OrganizationBillingPage = () => {
  const currentOrgUser = useSelfOrganizationUser();
  const isAdmin = currentOrgUser?.role === "ADMIN";
  const [org, ,] = usePrimaryOrganization();
  const [isCalendlyOpen, setIsCalendlyOpen] = useState(false);
  const [customerInfo, setCustomerInfo] = useState<ChargebeeCustomerInfoResponse | null>(null);
  const snackbar = useSnackBar();

  const getFeaturesList = (org: any) => {
    const features: string[] = [];

    if (org?.featureGdpr) {
      features.push("GDPR");
    }

    features.push(org?.featureMultistate ? "US" : "CCPA");

    if (org?.featurePipeda) {
      features.push("PIPEDA");
    }

    return features.length === 0 ? "No features" : lexicalJoin(features);
  };

  //load customer info
  useEffect(() => {
    const fetchCustomerInfo = async () => {
      if (!org || !org?.id) {
        return;
      }

      try {
        const data = await Api.chargebeeSession.getCustomerInfo(org.id);
        if (data.error) {
          console.error("Error in customer info response:", data.error);
          return;
        }
        setCustomerInfo(data);
      } catch (error) {
        console.error("Error fetching customer info:", error);
      }
    };
    fetchCustomerInfo();
  }, [org?.id]); // Updated dependency array

  const getFirstCardDisplay = (cards?: ChargebeeCardInfo[] | null): string | null => {
    if (cards === undefined || !cards?.length) {
      return null;
    }

    const [firstCard] = cards;
    // Capitalize the first letter of the brand
    const capitalizedBrand = firstCard.brand.charAt(0).toUpperCase() + firstCard.brand.slice(1);
    return `${capitalizedBrand} ending in ${firstCard.last4}`;
  };

  const handleContactUsClick = () => {
    setIsCalendlyOpen(true);
  };

  const handleCloseCalendly = () => {
    setIsCalendlyOpen(false);
  };
  const renderButtonWithTooltip = (isDisabled: boolean, text: string, onClick?: () => void) => {
    if (isDisabled) {
      return (
        <Tooltip title="You don't have access permission for this action. Contact your company's Admin for assistance.">
          <span className="tw-boundary">
            <Button color="primary" onClick={onClick} disabled={true}>
              {text}
            </Button>
          </span>
        </Tooltip>
      );
    }
    return (
      <div className="tw-boundary">
        <Button color="primary" onClick={onClick}>
          {text}
        </Button>
      </div>
    );
  };
  const handleManageAccountClick = async () => {
    try {
      // Create portal session using the API controller
      const portalSessionResponse = await Api.chargebeeSession.createPortalSession(org?.id);

      if (!portalSessionResponse.accessUrl) {
        throw new Error(portalSessionResponse.error || "No access token received");
      }

      let accessToken = portalSessionResponse.accessUrl;

      // Open the accessUrl in a popup window
      const width = 800;
      const height = 600;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;

      window.open(
        accessToken,
        "Chargebee Portal",
        `toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=yes, resizable=yes, copyhistory=no, width=${width}, height=${height}, top=${top}, left=${left}`,
      );
    } catch (error) {
      console.error("Error opening Chargebee portal:", error);
      snackbar(
        "Please contact support for your billing email or please email us at accounting@truevault.com to manage your billing details",
        {
          variant: "error",
          persist: true,
        },
      );
    }
  };

  return (
    <StandardPageWrapper>
      <div className="max-width-896">
        <PageHeader titleContent="Account Settings" marginBottomPx={0} />
        <SettingsTabs current="billing" />
        <div className="billing-content">
          <section className="billing-section">
            <div className="row-title">Billing Account</div>
            {customerInfo?.email && (
              <div className="row-body">
                Billing email: <b>{customerInfo.email}</b>
              </div>
            )}
            {getFirstCardDisplay(customerInfo?.cards) && (
              <div className="row-body">
                Payment method: <b>{getFirstCardDisplay(customerInfo?.cards)}</b>
              </div>
            )}
            <div className="button-row">
              {renderButtonWithTooltip(!isAdmin, "Manage", handleManageAccountClick)}
            </div>
          </section>
          <section className="billing-section">
            <div className="row-title">Your current plan includes: {getFeaturesList(org)}</div>
            <div className="row-body">
              Want to learn about our different plans or make changes to your current plan? <br />
              Get in touch with us at{" "}
              <a href="mailto:support@truevault.com">support@truevault.com</a> or click below
            </div>
            <div className="tw-boundary button-row">
              <Button color="primary" onClick={handleContactUsClick}>
                Contact Us
              </Button>
            </div>
          </section>
        </div>
      </div>
      {isCalendlyOpen && (
        <div className="modal-overlay" onClick={handleCloseCalendly}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <InlineWidget
              url="https://calendly.com/jillian-truevault/30min?hide_gdpr_banner=1&primary_color=5331c6"
              styles={{
                minWidth: "1400px",
                height: "900px",
                overflowX: "hidden",
                overflowY: "hidden",
                padding: "0",
                transform: "translateX(-150px)",
              }}
            />
          </div>
        </div>
      )}
    </StandardPageWrapper>
  );
};
